import React, { useState, useEffect } from 'react';
import {
  dashboardEmpresa,
  abrirArchivoURL,
  deleteAnexoEva
} from '../services/api';
import { formatRut } from '../utils/rut';
import { diasRestantesVigencia } from '../utils/vigencia';
import moment from 'moment';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Legend
} from 'recharts';
import AnexoModal from '../components/AnexoModal';
import AnexoViewerModal from '../components/AnexoViewerModal';
import { confirmar } from './ConfirmModal';
import { toast } from 'react-toastify';
import '../styles/styles.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

const PALETA_COLORES = ['#346400', '#5AAD00', '#FFBB28', '#FF4C5B', '#2E2E2E', '#A3A3A3'];

const EmpresaDashboard = () => {
  const [datosDashboard, setDatosDashboard] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorText, setErrorText] = useState(null);
  const [modalSubirAbierto, setModalSubirAbierto] = useState(false);
  const [evaluacionSeleccionada, setEvaluacionSeleccionada] = useState(null);
  
  // Anexo que se está visualizando en el AnexoViewerModal ({ idEva, anexo, pendiente })
  const [anexoVer, setAnexoVer] = useState(null);

  const cargarDashboard = async () => {
    try {
      setCargando(true);
      const res = await dashboardEmpresa();
      setDatosDashboard(res.data);
    } catch (err) {
      console.error('Error al cargar datos del dashboard de empresa:', err);
      setErrorText('No fue posible cargar las métricas del dashboard.');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarDashboard();
  }, []);

  const handleCerrarModalAnexo = () => {
    setAnexoVer(null);
  };

  const handleVerAnexo = (idEva, anexo, pendiente) => {
    setAnexoVer({ idEva, anexo, pendiente });
  };

  const abrirModalSubida = (pdf) => {
    setEvaluacionSeleccionada(pdf);
    setModalSubirAbierto(true);
  };

  const handleEliminarAnexo = async (idEva, idAnexo, e) => {
    if (e) e.stopPropagation(); // Evita propagar el click si está dentro de otro botón
    if (!(await confirmar('¿Estás seguro de que deseas eliminar este anexo?', { variante: 'peligro' }))) {
      return;
    }

    try {
      await deleteAnexoEva(idEva, idAnexo);
      if (anexoVer?.anexo?.id_anexo === idAnexo) {
        handleCerrarModalAnexo();
      }
      cargarDashboard();
    } catch (err) {
      console.error('Error al eliminar anexo:', err);
      toast.error(err.response?.data?.message || 'Error al eliminar el anexo.');
    }
  };

  if (cargando) {
    return (
      <div className="content-wrapper">
        <p>Cargando información del dashboard...</p>
      </div>
    );
  }

  if (errorText) {
    return (
      <div className="content-wrapper">
        <p className="required-text">{errorText}</p>
      </div>
    );
  }

  if (!datosDashboard) return null;

  const { empresa, resumen_evaluaciones, grafico_imc, grafico_rangos_etarios, grafico_patologias, pdfs } = datosDashboard;
  
  const imcData = [
    { name: 'Bajo Peso', valor: Number(grafico_imc?.bajo_peso ?? 0), fill: PALETA_COLORES[0] },
    { name: 'Normal', valor: Number(grafico_imc?.normal ?? 0), fill: PALETA_COLORES[1] },
    { name: 'Sobrepeso', valor: Number(grafico_imc?.sobrepeso ?? 0), fill: PALETA_COLORES[2] },
    { name: 'Obesidad I', valor: Number(grafico_imc?.obesidad_grado_1 ?? 0), fill: PALETA_COLORES[3] },
    { name: 'Obesidad II', valor: Number(grafico_imc?.obesidad_grado_2 ?? 0), fill: PALETA_COLORES[4] },
    { name: 'Obesidad III', valor: Number(grafico_imc?.obesidad_grado_3 ?? 0), fill: PALETA_COLORES[5] },
  ];

  const etariosData = [
    {
      name: '18-39 años',
      valor: Number(grafico_rangos_etarios?.['18-39'] ?? grafico_rangos_etarios?.rango_18_39 ?? 0)
    },
    {
      name: '40-54 años',
      valor: Number(grafico_rangos_etarios?.['40-54'] ?? grafico_rangos_etarios?.rango_40_54 ?? 0)
    },
    {
      name: '55+ años',
      valor: Number(grafico_rangos_etarios?.['55+'] ?? grafico_rangos_etarios?.rango_55_mas ?? 0)
    },
  ];

  const patologiasData = [
    { name: 'Diabetes T1', total: Number(grafico_patologias?.diabetes_t1 ?? 0) },
    { name: 'Diabetes T2', total: Number(grafico_patologias?.diabetes_t2 ?? 0) },
    { name: 'Hipertensión', total: Number(grafico_patologias?.hiper_arterial ?? 0) },
    { name: 'Asma', total: Number(grafico_patologias?.asma ?? 0) },
    { name: 'Insuf. Cardíaca', total: Number(grafico_patologias?.insuf_card ?? 0) },
    { name: 'Insuf. Resp.', total: Number(grafico_patologias?.insuf_resp ?? 0) },
    { name: 'Síncope/Mareos', total: Number(grafico_patologias?.sincope_mareos ?? 0) },
    { name: 'Epilepsia', total: Number(grafico_patologias?.epilepsia ?? 0) },
  ];

  return (
    <div className="content-wrapper">
      <h1>Dashboard Empresa</h1>
      <h2>{empresa?.nombre_legal}</h2>
      <p><strong>RUT:</strong> {empresa?.rut ? formatRut(empresa.rut) : 'Sin RUT'} | <strong>Contacto:</strong> {empresa?.email}</p>

      {/* Tarjetas Resumen */}
      <div className="exam-grid">
        <div className="exam-card">
          <h3>Total Evaluaciones</h3>
          <h2>{resumen_evaluaciones?.total_evaluaciones ?? 0}</h2>
        </div>
        <div className="exam-card">
          <h3>Vigentes</h3>
          <h2 style={{ color: '#5AAD00' }}>{resumen_evaluaciones?.vigentes ?? 0}</h2>
        </div>
        <div className="exam-card">
          <h3>Vencidas</h3>
          <h2 style={{ color: '#FF4C5B' }}>{resumen_evaluaciones?.vencidas ?? 0}</h2>
        </div>
        <div className="exam-card">
          <h3>Pendientes</h3>
          <h2 style={{ color: '#FFBB28' }}>{resumen_evaluaciones?.pendientes ?? 0}</h2>
        </div>
      </div>

      {/* Gráficos */}
      <div className="container">
        <h2>Métricas Ocupacionales</h2>
        <div className="examenes-layout">
          <div className="examenes-left">
            <h3>Índice de Masa Corporal (IMC)</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={imcData} dataKey="valor" nameKey="name" cx="50%" cy="50%" outerRadius={80} label />
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="examenes-right" style={{ minWidth: 0, width: '100%' }}>
            <h3>Distribución Etaria</h3>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={etariosData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} domain={[0, 'auto']} />
                  <Tooltip />
                  <Bar dataKey="valor" fill="#346400" label={{ position: 'top', fill: '#2E2E2E', fontSize: 14 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="examen-container-item">
          <h3>Prevalencia de Patologías</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={patologiasData} layout="vertical">
              <XAxis type="number" allowDecimals={false} domain={[0, 'dataMax + 1']} />
              <YAxis dataKey="name" type="category" width={130} />
              <Tooltip />
              <Bar dataKey="total" fill="#5AAD00" label={{ position: 'right', fill: '#2E2E2E' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tabla de Fichas Médicas y Anexos */}
      <div className="container">
        <h2>Fichas Médicas (PDFs) y Anexos</h2>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Paciente</th>
                <th>RUT Paciente</th>
                <th>Vigencia</th>
                <th>Ficha Médica</th>
                <th>Anexos Adjuntos</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(pdfs) && pdfs.length > 0 ? (
                pdfs.map((pdf) => {
                  const diasRestantes = diasRestantesVigencia(pdf.fecha_vencimiento);
                  return (
                  <tr key={pdf.id_pdf ?? `eva-${pdf.id_eva}`} className={pdf.pendiente ? "evaluacion-pendiente" : ""}>
                    <td>{pdf.fecha ? moment(pdf.fecha).format('DD-MM-YYYY') : '-'}</td>
                    <td>{pdf.paciente_nombre}</td>
                    <td>{pdf.paciente_rut ? formatRut(pdf.paciente_rut) : '-'}</td>
                    <td>
                      {diasRestantes === null
                        ? '-'
                        : diasRestantes >= 0
                          ? `${diasRestantes} días`
                          : 'Vencida'}
                    </td>
                    <td>
                      {pdf.archivo_url ? (
                        <button
                          type="button"
                          onClick={() => abrirArchivoURL(pdf.archivo_url)}
                          className="btn-icon"
                        >
                          <i className="fas fa-eye"></i> Ver PDF
                        </button>
                      ) : (
                        <span style={{ color: '#888', fontSize: '0.85rem' }}>Ficha aún no generada</span>
                      )}
                    </td>
                    <td>
                      {Array.isArray(pdf.anexos) && pdf.anexos.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {pdf.anexos.map((anexo) => (
                            <div 
                              key={anexo.id_anexo} 
                              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                              {/* Botón para abrir modal de vista previa de anexo vía Blob */}
                              <button
                                type="button"
                                onClick={() => handleVerAnexo(pdf.id_eva, anexo, pdf.pendiente)}
                                className="btn-icon btn-secondary"
                                style={{ fontSize: '0.85rem', textAlign: 'left', flexGrow: 1 }}
                                title={anexo.observaciones || anexo.original_name}
                              >
                                <i className="fas fa-paperclip"></i>{' '}
                                <span className="filename-truncado">{anexo.original_name || anexo.filename}</span>
                              </button>

                              {/* Borrar solo se puede mientras la evaluación siga pendiente */}
                              {pdf.pendiente && (
                                <button
                                  type="button"
                                  onClick={(e) => handleEliminarAnexo(pdf.id_eva, anexo.id_anexo, e)}
                                  className="btn-icon"
                                  style={{ backgroundColor: '#dc2626', color: '#fff', padding: '4px 8px' }}
                                  title="Eliminar este anexo"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#888', fontSize: '0.85rem' }}>Sin anexos</span>
                      )}
                    </td>
                    <td>
                      {pdf.pendiente ? (
                        <button
                          type="button"
                          onClick={() => abrirModalSubida(pdf)}
                          className="btn-icon btn-primary"
                          style={{ fontSize: '0.85rem' }}
                        >
                          <i className="fas fa-upload"></i> Subir Anexo
                        </button>
                      ) : (
                        <span style={{ color: '#888', fontSize: '0.85rem' }} title="La ficha ya fue revisada, no se requieren más anexos">
                          <i className="fas fa-check"></i> Sin pendientes
                        </span>
                      )}
                    </td>
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center' }}>
                    No existen fichas médicas en PDF disponibles.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Subir Anexo */}
      <AnexoModal
        isOpen={modalSubirAbierto}
        onClose={() => setModalSubirAbierto(false)}
        idEva={evaluacionSeleccionada?.id_eva}
        pacienteNombre={evaluacionSeleccionada?.paciente_nombre}
        onSuccess={() => cargarDashboard()}
      />

      {/* Modal Visualizador de Anexo Seguro (Blob) */}
      {anexoVer && (
        <AnexoViewerModal
          idEva={anexoVer.idEva}
          anexo={anexoVer.anexo}
          onClose={handleCerrarModalAnexo}
          onDelete={anexoVer.pendiente ? (e) => handleEliminarAnexo(anexoVer.idEva, anexoVer.anexo.id_anexo, e) : undefined}
        />
      )}
    </div>
  );
};

export default EmpresaDashboard;